import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Every ordinary call here is one Mongo query. 15s survives a slow link or a
// cold container and still surfaces a stuck request while the user is looking
// at the screen. The handful of calls that legitimately run longer pass their
// own timeout from TIMEOUTS below — the default is the rule, not the ceiling.
//
// Note this does NOT cover the two streaming endpoints: /atena/chat and
// /ai/chat go through fetch, which axios cannot time out. They have no time
// limit at all; giving them one needs an AbortController.
export const DEFAULT_TIMEOUT_MS = 15000;

export const TIMEOUTS = {
  // axios counts upload time against `timeout`, so any fixed value cuts off a
  // large file on a slow uplink. onUploadProgress is the liveness signal here.
  upload: 0,
  avatarUpload: 60000,
  export: 120000,      // renders a PDF over up to 5000 tickets
  digestSend: 120000,  // composes the digest and sends mail
  atenaAction: 60000,  // runs a confirmed tool, may call a model
  // Dictarea: urcarea sunetului, plus coada, plus transcrierea. Măsurat pe 3
  // secunde de sunet — 2,8 s cu modelul cald, 5,6 s când e una în față. Limita
  // serverului e de 120 s de sunet, deci termenul acoperă cazul cel mai lung
  // plus așteptarea, nu media.
  dictate: 180000,
  // O replică din bucla de voce: aceeași urcare și aceeași coadă ca la dictare,
  // plus transcrierea cu modelul `medium` (mai lent decât `small`, dar singurul
  // care nu strică fraze în română), plus răspunsul Atenei — care poate rula o
  // unealtă. Deci termenul dictării plus timpul unui răspuns, nu al unei
  // transcrieri.
  voiceTurn: 300000,
};

export const api = axios.create({ baseURL: API, timeout: DEFAULT_TIMEOUT_MS });

export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    localStorage.setItem("vs_token", token);
  } else {
    delete api.defaults.headers.common["Authorization"];
    localStorage.removeItem("vs_token");
  }
}

export function getToken() {
  return localStorage.getItem("vs_token");
}

const existing = getToken();
if (existing) api.defaults.headers.common["Authorization"] = `Bearer ${existing}`;

/**
 * The handshake URL, given a single-use ticket.
 *
 * It used to take the session token, which put the whole account in the URL of
 * every websocket handshake and so in every proxy log it passed. The browser's
 * WebSocket API cannot send a header, so something travels in the URL — a
 * ticket is worth nothing after one use and nothing after thirty seconds.
 *
 * There is deliberately no overload that accepts a session token. A fallback is
 * the defect, waiting for the first time the ticket endpoint is slow.
 */
export function buildWsUrl(ticket) {
  return `${BACKEND_URL.replace(/^http/, "ws")}/api/ws?ticket=${encodeURIComponent(ticket)}`;
}

/**
 * How long to wait before the next attempt.
 *
 * The first is immediate. A socket that closed because the laptop was asleep
 * should come back when the laptop does, and waiting a second there is a second
 * of a chat app looking broken — which was the shape of the original schedule,
 * where attempt 0 already waited 1000ms.
 *
 * After that it doubles and caps, so a server that is down is not asked for a
 * ticket ten times a second by every open tab.
 */
export function wsBackoffMs(attempt) {
  if (attempt <= 0) return 0;
  return Math.min(1000 * 2 ** (attempt - 1), 30000);
}

/** A ticket for one handshake. Minted by the server; never derived here. */
export async function requestWsTicket(workspace_id) {
  const { data } = await api.post("/ws/ticket", { workspace_id: workspace_id || null });
  return data.ticket;
}

// fileUrl is gone on purpose. It put the session JWT in the query string of
// every rendered image; see lib/fileAccess.js for what replaces it, and
// __tests__/urlCredentials.test.js for the rule that stops it coming back.

/**
 * What actually went wrong, with the number in it.
 *
 * "eroare la încărcare" on an operation that takes minutes is the worst place
 * in the app for a generic message: the person has already spent the time, and
 * the message spends it again by telling them nothing.
 */
export function uploadErrorMessage(error, maxBytes, fileSize) {
  const mb = (n) => `${Math.round(n / (1024 * 1024))} MB`;
  const status = error?.response?.status;
  if (status === 413) {
    return maxBytes
      ? `Fișierul are ${mb(fileSize || 0)}, maximul este ${mb(maxBytes)}.`
      : "Fișierul depășește dimensiunea maximă acceptată.";
  }
  if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) {
    return "Încărcarea a depășit timpul alocat. Conexiunea a fost prea lentă sau s-a întrerupt.";
  }
  if (status === 405 || status === 404) {
    return "Ruta de încărcare nu există pe server — semnalează asta, e un bug, nu o limită.";
  }
  if (status === 401 || status === 403) return "Nu ai dreptul să încarci în acest workspace.";
  if (!status) return "Conexiunea s-a întrerupt în timpul încărcării.";
  return error?.response?.data?.detail || `Încărcarea a eșuat (HTTP ${status}).`;
}

// Cât arată bara cât timp mai e ceva de făcut. Browserul raportează octeții
// predați socketului, deci ajunge la 100% în timp ce serverul încă scrie în
// GridFS și încă nu a răspuns. O bară care sare la 100% și apoi stă arată ca o
// aplicație blocată; una care stă la 99 până vine răspunsul spune adevărul.
export const UPLOAD_SENT_CAP = 99;

export async function uploadFile(workspace_id, file, onProgress) {
  const fd = new FormData();
  fd.append("workspace_id", workspace_id);
  fd.append("file", file);
  const r = await api.post("/uploads", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: TIMEOUTS.upload,
    onUploadProgress: (e) => {
      const total = e.total || file.size || 0;
      const sent = Math.min(e.loaded || 0, total || e.loaded || 0);
      const percent = total ? Math.round((sent / total) * 100) : 0;
      // Al doilea argument e pentru cine vrea să scrie „312 MB din 500 MB":
      // pe o încărcare de minute, o cifră care se mișcă e singura dovadă că
      // lucrul merge înainte.
      onProgress?.(Math.min(percent, UPLOAD_SENT_CAP), { loaded: sent, total });
    },
  });
  onProgress?.(100, { loaded: file.size || 0, total: file.size || 0 });
  return r.data;
}

export async function streamAIChat({ prompt, session_id, provider, workspace_id, onDelta, onDone, onError, onSources }) {
  const token = getToken();
  try {
    const resp = await fetch(`${API}/ai/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ prompt, session_id, provider, workspace_id }),
    });
    if (!resp.ok || !resp.body) { onError?.(`HTTP ${resp.status}`); return; }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf("\n\n")) !== -1) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const line = chunk.split("\n").find((l) => l.startsWith("data:"));
        if (!line) continue;
        const data = line.slice(5).trim();
        if (data === "[DONE]") { onDone?.(); return; }
        try {
          const ev = JSON.parse(data);
          if (ev.type === "delta") onDelta?.(ev.content);
          else if (ev.type === "sources") onSources?.(ev.content);
          else if (ev.type === "error") onError?.(ev.content);
        } catch (_e) { /* ignore */ }
      }
    }
    onDone?.();
  } catch (e) { onError?.(e.message); }
}

export async function streamOpsClaude({ prompt, channelId, onDelta, onDone, onError }) {
  const token = getToken();
  try {
    const resp = await fetch(`${API}/ops/claude`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ prompt, channel_id: channelId }),
    });
    if (!resp.ok || !resp.body) {
      let detail = `HTTP ${resp.status}`;
      try {
        const body = await resp.json();
        if (body?.detail) detail = body.detail;
      } catch (_e) { /* response was not JSON */ }
      onError?.(detail);
      return;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf("\n\n")) !== -1) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const line = chunk.split("\n").find((item) => item.startsWith("data:"));
        if (!line) continue;
        const data = line.slice(5).trim();
        if (data === "[DONE]") {
          onDone?.();
          return;
        }
        try {
          const event = JSON.parse(data);
          if (event.type === "delta") onDelta?.(event.content);
          else if (event.type === "error") onError?.(event.content);
        } catch (_e) { /* ignore malformed stream events */ }
      }
    }
    onDone?.();
  } catch (e) {
    onError?.(e.message);
  }
}
